import { Cta } from "@/components/Cta";
import { MediaPlaceholder } from "@/components/MediaPlaceholder";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Section } from "@/components/Section";
import { SectionHeader } from "@/components/SectionHeader";
import { TerminalCard } from "@/components/TerminalCard";
import { useSeo } from "@/lib/seo";
import {
  Card,
  CodeBlock,
  Container,
  Grid,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@repo/ui/core";
import {
  IconCheck,
  IconClock,
  IconCloud,
  IconCode,
  IconDeviceDesktop,
  IconStack2,
  IconTerminal,
} from "@repo/ui/icons";
import styles from "@styles/site.module.css";

const features = [
  {
    title: "Instant Onboarding",
    description:
      "New developers and contractors get productive in minutes with browser-based access.",
    icon: IconClock,
  },
  {
    title: "Browser-Based IDE",
    description:
      "Launch a full IDE for remote development with one click. Build and test from the web.",
    icon: IconCode,
  },
  {
    title: "Custom Environments",
    description:
      "Customize your environment with devfiles per repo or per user. Everyone gets their ideal setup.",
    icon: IconStack2,
  },
  {
    title: "Multi-Region Infrastructure",
    description:
      "Localized infrastructure for global teams. US and EU regions today, APAC on the roadmap.",
    icon: IconCloud,
  },
  {
    title: "Local Connectivity",
    description:
      "SSH access for when you need it. Use your local tools with cloud compute.",
    icon: IconTerminal,
  },
  {
    title: "Emulator Integration",
    description:
      "View CI status, create emulators, and connect, all from your workspace.",
    icon: IconDeviceDesktop,
  },
];

const noHardwarePoints = [
  "No local tools to install",
  "No hardware to ship",
  "No environment configuration",
  "Secure access through GitLab",
];

const emulatorPoints = [
  "View all running emulators",
  "One-click connection",
  "Launch from any branch or build",
  "Integrated with CI pipeline status",
];

const devfilePoints = [
  "Repository-level devfiles",
  "User preference overrides",
  "Pre-installed extensions",
  "Custom shell configurations",
];

function CheckList({ items }: { items: string[] }) {
  return (
    <ul className={styles.checkList}>
      {items.map((item) => (
        <li key={item}>
          <IconCheck size={16} stroke={2.5} />
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function RemoteDev() {
  useSeo("/products/remote-dev");

  return (
    <>
      <PageHero
        eyebrow="Remote Development"
        title={
          <>
            Onboard developers in{" "}
            <span className={styles.gradientText}>minutes</span>
          </>
        }
        description="A full IDE in the browser via GitLab Workspaces, preconfigured per repo with devfiles. No local tools to install, no workstation to ship."
      >
        <Cta to="/contact">Book a Demo</Cta>
      </PageHero>

      {/* Feature grid */}
      <Section>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {features.map((feat, i) => (
            <Reveal key={feat.title} delay={(i % 3) * 0.05}>
              <Card
                padding="xl"
                className={styles.card}
                style={{ height: "100%" }}
              >
                <Stack gap="md">
                  <span className={styles.iconChip}>
                    <feat.icon size={20} stroke={1.5} />
                  </span>
                  <Title order={3} size="1.125rem" fw={600}>
                    {feat.title}
                  </Title>
                  <Text c="dimmed" size="sm" lh={1.7}>
                    {feat.description}
                  </Text>
                </Stack>
              </Card>
            </Reveal>
          ))}
        </SimpleGrid>
      </Section>

      {/* Stop shipping laptops */}
      <Section hairline>
        <Grid gutter={64} align="center">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <SectionHeader
              title="Stop shipping laptops"
              description="Traditional automotive development requires expensive workstations and weeks of environment setup. With Null Autos, contractors get productive on day one from any device."
            />
            <Reveal>
              <CheckList items={noHardwarePoints} />
            </Reveal>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Reveal delay={0.08} y={24}>
              <div className={styles.mediaFrame}>
                <MediaPlaceholder
                  label="GitLab Workspace IDE in the browser, AAOSP repo open on a cloud workspace"
                  ratio="16 / 10"
                  src="/null/screenshot.dev.png"
                />
                <div className={styles.frameCaption}>
                  GitLab Workspaces / browser IDE
                </div>
              </div>
            </Reveal>
          </Grid.Col>
        </Grid>
      </Section>

      {/* Emulators at your fingertips */}
      <Section hairline>
        <Grid gutter={64} align="center">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Reveal y={24}>
              <div className={styles.mediaFrame}>
                <MediaPlaceholder
                  label="Emulator list view with CI status chips and connect actions"
                  ratio="16 / 10"
                  src="/null/screenshot.dev.ext.png"
                />
                <div className={styles.frameCaption}>
                  Emulators / workspace view
                </div>
              </div>
            </Reveal>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <SectionHeader
              title="Emulators at your fingertips"
              description="Create, connect, and manage emulators directly from your workspace. See CI status, launch new instances, and debug, all without switching contexts."
            />
            <Reveal>
              <CheckList items={emulatorPoints} />
            </Reveal>
          </Grid.Col>
        </Grid>
      </Section>

      {/* Devfile: stacked, breaks the zigzag */}
      <Section hairline>
        <SectionHeader
          align="center"
          title="Your environment, your way"
          description="Customize workspaces with devfiles per repo or per user. Define tools, extensions, and configurations that spin up automatically."
        />
        <Reveal>
          <div style={{ maxWidth: 720, margin: "0 auto 40px" }}>
            <CheckList items={devfilePoints} />
          </div>
        </Reveal>
        <Reveal delay={0.08} y={24}>
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <TerminalCard title=".devfile.yaml">
              <CodeBlock
                code={`# .devfile.yaml
schemaVersion: 2.2.0

components:
  - name: android-tools
    container:
      image: null.autos/android:14
      memoryLimit: 16Gi
      env:
        - name: ANDROID_HOME
          value: /opt/android-sdk`}
                language="yaml"
                withCopyButton={false}
                style={{ fontSize: "0.8125rem", background: "transparent" }}
                styles={{
                  pre: {
                    background: "transparent",
                    padding: 0,
                    margin: 0,
                    lineHeight: 1.8,
                  },
                }}
              />
            </TerminalCard>
          </div>
        </Reveal>
      </Section>

      {/* CTA */}
      <section className={styles.finalCta}>
        <Container size="xl" py={88} ta="center">
          <Reveal>
            <Title
              order={2}
              fw={650}
              size="2rem"
              style={{ letterSpacing: "-0.02em" }}
            >
              Day-one productivity for every developer
            </Title>
            <Text
              size="lg"
              c="dimmed"
              maw={480}
              mx="auto"
              mt="md"
              mb="xl"
              lh={1.65}
            >
              Watch a contractor go from invite to running emulator in one
              session.
            </Text>
            <div className={styles.ctaRow} style={{ justifyContent: "center" }}>
              <Cta to="/contact">Book a Demo</Cta>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
}
